'use client';

import React, { useState, useRef, useEffect } from 'react';
import type { PassengerCounts } from './FlightSearchHero';

interface PassengerSelectorProps {
  passengers: PassengerCounts;
  onChange: (p: PassengerCounts) => void;
}

export default function PassengerSelector({ passengers, onChange }: PassengerSelectorProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const total = passengers.adults + passengers.children + passengers.infants;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const adjust = (type: keyof PassengerCounts, delta: number) => {
    const next = { ...passengers, [type]: Math.max(type === 'adults' ? 1 : 0, passengers[type] + delta) };
    if (next.adults + next.children + next.infants > 9) return;
    onChange(next);
  };

  const rows: { key: keyof PassengerCounts; label: string; sub: string; min: number }[] = [
    { key: 'adults', label: 'Adults', sub: '18+ years', min: 1 },
    { key: 'children', label: 'Children', sub: '2–17 years', min: 0 },
    { key: 'infants', label: 'Infants', sub: 'Under 2', min: 0 },
  ];

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-4 py-2.5 border border-border rounded-xl bg-input hover:border-primary text-sm font-medium text-foreground transition-all duration-150"
      >
        <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span>
          {total} {total === 1 ? 'Passenger' : 'Passengers'}
        </span>
        <svg className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full mt-1.5 left-0 bg-card border border-border rounded-xl card-shadow z-50 w-72 p-4 fade-in">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Passengers (max 9)</p>
          <div className="space-y-4">
            {rows.map((row) => (
              <div key={`pax-${row.key}`} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-foreground">{row.label}</p>
                  <p className="text-xs text-muted-foreground">{row.sub}</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => adjust(row.key, -1)}
                    disabled={passengers[row.key] <= row.min}
                    className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-foreground hover:border-primary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-90"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
                    </svg>
                  </button>
                  <span className="w-4 text-center text-sm font-semibold text-foreground font-tabular">
                    {passengers[row.key]}
                  </span>
                  <button
                    type="button"
                    onClick={() => adjust(row.key, 1)}
                    disabled={total >= 9}
                    className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-foreground hover:border-primary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-90"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-4 w-full py-2 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:bg-sky-600 transition-colors duration-150"
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}